/**
 * Local prediction and server reconciliation around `@rogueworlds/sim`.
 *
 * The rules this module enforces:
 * - The client predicts with the SAME code as the server (`stepPlayer` = `integrate` + `resolveCollision`)
 *   at exactly `1 / tickHz`, against a `SolidQuery` backed by `ClientWorld.blockAt`.
 * - The client never moves itself from a `snapshot`. Only `ack` (the server's authoritative state for a
 *   `seq` we sent) reconciles, through the frozen pure `reconcile()`.
 * - Client tick numbers are seeded from `welcome.tick` so the client's tick axis shares the server's
 *   reference; otherwise `reconcile`'s `entry.tick <= authoritative.tick` comparison would either drop
 *   every history entry (rubber-banding) or never drop one (no correction at all).
 * - A small render-only smoothing offset hides the correction. It is added when the position is read
 *   for rendering and NEVER written back into the simulated state.
 */

import type { PlayerState, Vec3 } from "@rogueworlds/shared";
import {
  EYE_HEIGHT_M,
  reconcile,
  stepPlayer,
  toPlayerState,
  type PlayerCommand,
  type PlayerSimState,
  type SolidQuery,
} from "@rogueworlds/sim";

/** `ack` frame fields used for reconciliation. */
export interface PredictionAck {
  readonly seq: number;
  readonly tick: number;
  readonly pos: Vec3;
  readonly vel: Vec3;
}

/** Predicted states kept for replay: 128 ticks = 6.4 s at 20 Hz. */
const HISTORY_LIMIT = 128;
/** Smoothing offset decay, per second (linear: no Math.exp, matching the sim's rule set). */
const SMOOTH_DECAY_PER_S = 14;
/** Largest offset rendered as "smoothing"; anything past this is a snap. */
const SMOOTH_MAX_M = 0.6;
/** Corrections larger than this snap instantly instead of flying. */
const SMOOTH_SNAP_M = 2;

export class Prediction {
  private readonly state: PlayerSimState;
  private readonly history: PlayerState[] = [];
  private readonly authoritativeScratch: PlayerState;
  private readonly tickHz: number;
  private readonly solid: SolidQuery;

  private smoothX = 0;
  private smoothY = 0;
  private smoothZ = 0;
  private replayedTicks = 0;
  private lastCorrectionM = 0;
  private lastAckSeq = 0;

  /**
   * @param startTick the server tick from `welcome`, so the local tick axis shares the server's origin.
   */
  constructor(id: string, name: string, spawn: Vec3, startTick: number, tickHz: number, solid: SolidQuery) {
    this.tickHz = tickHz;
    this.solid = solid;
    this.state = {
      id,
      name,
      pos: { x: spawn.x, y: spawn.y, z: spawn.z },
      vel: { x: 0, y: 0, z: 0 },
      yaw: 0,
      pitch: 0,
      tick: startTick,
      grounded: false,
    };
    this.authoritativeScratch = {
      id,
      name,
      pos: { x: spawn.x, y: spawn.y, z: spawn.z },
      yaw: 0,
      pitch: 0,
      tick: startTick,
    };
  }

  /** The live predicted state. Read-only by convention; the loop owns mutation through `step`. */
  get predicted(): PlayerSimState {
    return this.state;
  }

  /** Server ticks re-applied by the last reconciliation (diagnostics). */
  get replayed(): number {
    return this.replayedTicks;
  }

  /** Size of the last visual correction in meters (diagnostics). */
  get correctionM(): number {
    return this.lastCorrectionM;
  }

  get lastSeq(): number {
    return this.lastAckSeq;
  }

  get yaw(): number {
    return this.state.yaw;
  }

  get pitch(): number {
    return this.state.pitch;
  }

  /** One fixed tick of local prediction. Call exactly once per `1 / tickHz` step. */
  step(command: PlayerCommand): void {
    stepPlayer(this.state, command, this.tickHz, this.solid);
    this.history.push(toPlayerState(this.state));
    if (this.history.length > HISTORY_LIMIT) {
      this.history.splice(0, this.history.length - HISTORY_LIMIT);
    }
  }

  /**
   * Adopts the server's authoritative state and replays whatever the client predicted past it.
   * Never call this with a `snapshot`: it would fight prediction and teleport the player.
   */
  onAck(ack: PredictionAck): void {
    this.lastAckSeq = ack.seq;
    const rendered = this.renderPosition({ x: 0, y: 0, z: 0 });

    const authoritative = this.authoritativeScratch;
    authoritative.pos.x = ack.pos.x;
    authoritative.pos.y = ack.pos.y;
    authoritative.pos.z = ack.pos.z;
    authoritative.yaw = this.state.yaw;
    authoritative.pitch = this.state.pitch;
    authoritative.tick = ack.tick;

    const result = reconcile(toPlayerState(this.state), authoritative, this.history);
    const adopted = result.state;
    this.replayedTicks = result.replayed;

    this.state.pos.x = adopted.pos.x;
    this.state.pos.y = adopted.pos.y;
    this.state.pos.z = adopted.pos.z;
    this.state.yaw = adopted.yaw;
    this.state.pitch = adopted.pitch;
    this.state.tick = adopted.tick;
    if (adopted.tick <= ack.tick) {
      // Nothing newer than the server state survived: the server's velocity is the better estimate.
      this.state.vel.x = ack.vel.x;
      this.state.vel.y = ack.vel.y;
      this.state.vel.z = ack.vel.z;
    }

    // History entries the server has already superseded are dead weight.
    let drop = 0;
    while (drop < this.history.length) {
      const entry = this.history[drop];
      if (entry === undefined || entry.tick > ack.tick) {
        break;
      }
      drop += 1;
    }
    if (drop > 0) {
      this.history.splice(0, drop);
    }

    this.applyCorrection(rendered);
  }

  /** Decay the render-only smoothing offset. Call once per frame with the frame delta in seconds. */
  decaySmoothing(dtSeconds: number): void {
    const factor = 1 - SMOOTH_DECAY_PER_S * dtSeconds;
    const clamped = factor < 0 ? 0 : factor > 1 ? 1 : factor;
    this.smoothX *= clamped;
    this.smoothY *= clamped;
    this.smoothZ *= clamped;
    if (Math.abs(this.smoothX) < 0.0005) {
      this.smoothX = 0;
    }
    if (Math.abs(this.smoothY) < 0.0005) {
      this.smoothY = 0;
    }
    if (Math.abs(this.smoothZ) < 0.0005) {
      this.smoothZ = 0;
    }
  }

  /** Feet position for rendering: authoritative position plus the decaying offset. */
  renderPosition(out: Vec3): Vec3 {
    out.x = this.state.pos.x + this.smoothX;
    out.y = this.state.pos.y + this.smoothY;
    out.z = this.state.pos.z + this.smoothZ;
    return out;
  }

  /** Camera/eye position for rendering, the raycast origin and the sun target. */
  eyePosition(out: Vec3): Vec3 {
    this.renderPosition(out);
    out.y += EYE_HEIGHT_M;
    return out;
  }

  private applyCorrection(rendered: Vec3): void {
    const dx = rendered.x - this.state.pos.x;
    const dy = rendered.y - this.state.pos.y;
    const dz = rendered.z - this.state.pos.z;
    const distance = Math.sqrt(dx * dx + dy * dy + dz * dz);
    this.lastCorrectionM = distance;
    if (distance < 0.0005 || distance > SMOOTH_SNAP_M) {
      // No correction worth hiding, or a correction too large to hide: snap.
      this.smoothX = 0;
      this.smoothY = 0;
      this.smoothZ = 0;
      return;
    }
    const scale = distance > SMOOTH_MAX_M ? SMOOTH_MAX_M / distance : 1;
    this.smoothX = dx * scale;
    this.smoothY = dy * scale;
    this.smoothZ = dz * scale;
  }
}
